import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};

// Load wishlist from localStorage (runs once on init)
const loadWishlistFromStorage = () => {
  if (typeof window === 'undefined') return [];
  const stored = localStorage.getItem('wishlist');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Error loading wishlist:', e);
      return [];
    }
  }
  return [];
};

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState(loadWishlistFromStorage);

  // Save to localStorage whenever wishlist changes
  useEffect(() => {
    localStorage.setItem('wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  const addToWishlist = (product) => {
    if (!product || !product.id) return;
    
    setWishlist((prev) => {
      // Check if already in wishlist
      if (prev.some(p => p.id === product.id)) {
        return prev;
      }
      return [...prev, {
        id: product.id,
        slug: product.slug,
        name: product.name,
        price: product.price,
        image: product.image,
        addedAt: new Date().toISOString()
      }];
    });
  };

  const removeFromWishlist = (productId) => {
    setWishlist((prev) => prev.filter(p => p.id !== productId));
  };

  const isInWishlist = (productId) => {
    return wishlist.some(p => p.id === productId);
  };

  const toggleWishlist = (product) => {
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
      return false;
    } else {
      addToWishlist(product);
      return true;
    }
  };

  const getWishlistCount = () => wishlist.length;

  const clearWishlist = () => setWishlist([]);

  return (
    <WishlistContext.Provider value={{
      wishlist,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      toggleWishlist,
      getWishlistCount,
      clearWishlist
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistContext;
